import { Box, Container, Typography, styled } from "@mui/material";
import React, { FC } from "react";
import { Helmet } from 'react-helmet-async'
import LayoutPlain from "@/pages/authentication/LayoutPlain";


export const LegalTextContent = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(16),
  '& h3': {
    fontSize: theme.typography.h5.fontSize,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  '& p': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  '& ul.unstyled': {
    listStyle: 'none',
    '& > li': { textIndent: '-1.5em' },
  },
}))



const CustomerAgreement: FC = () => {
  return (
    <LayoutPlain withSiginIn={true}>
      <Helmet>
        <title>Customer Agreement | OnData</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: 'background.paper',
          minHeight: '100%',
          pt: 6,
          pb: 4,
          mb: 4,
        }}
      >
        <Container maxWidth="md">
          <Typography
            color="textPrimary"
            align="center"
            variant="h2"
            sx={{
              mt: 6,
            }}
          >
            OnData Customer Agreement
          </Typography>
          <Typography
            // color="textPrimary"
            component="p"
            align="center"
            color="textSecondary"
            variant="subtitle1"
          >
            (Last Updated: August 16, 2021)
          </Typography>

          <LegalTextContent>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              This OnData Customer Agreement (this “Agreement”) contains the terms and conditions that govern your access to and use of the OnData Services and is an agreement between OnData, Inc. (also referred to as “OnData”, “we”, “us”, or “our”) and you or the entity you represent (“you” or “your”). This Agreement takes effect when you sign up as an OnData user (the “Effective Date”). You represent to us that you are lawfully able to enter contracts. If you are entering into this Agreement for an entity, such as the organization you work for, you represent to us that you have legal authority to bind that entity.
            </Typography>

            <Typography
              color="textPrimary"
              id="access-to-and-usage-of-ondata-services"
              variant="h3"
            >
              1. Access to and Usage of OnData Services
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              1.1 You may access and use the OnData Services in accordance with this Agreement. You will comply with the terms of this Agreement and all laws, rules, and regulations applicable to your use of the OnData Services. To access the OnData Services, you must have an OnData account associated with a valid email address and a valid form of payment.
            </Typography>

            <Typography
              color="textPrimary"
              id="changes-of-ondata-services"
              variant="h3"
            >
              2. Changes of OnData Services
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              2.1 We may change or discontinue any of the OnData Services from time to time. We will provide you at least 6-month prior notice if we discontinue material functionality of an OnData Service that you are using, except that this notice will not be required if the 6-month notice period (a) would pose a security or intellectual property issue to us or the OnData Services, (b) is economically or technically burdensome, or (c) would cause us to violate legal requirements.
            </Typography>

            <Typography
              color="textPrimary"
              id="security-and-data-privacy"
              variant="h3"
            >
              3. Security and Data Privacy
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              3.1 Without limiting Section 10 or your obligations under Section 4.2, we will implement reasonable and appropriate measures designed to help you secure Your Content against accidental or unlawful loss, access, or disclosure.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              3.2 We will not access or use Your Content except as necessary to maintain or provide the OnData Services, or as necessary to comply with the law or a binding order of a governmental body. We will not disclose Your Content to any third party. We will only use your account information in accordance with the Privacy Notice, and you consent to such usage. The Privacy Notice does not apply to Your Content.
            </Typography>

            <Typography
              color="textPrimary"
              id="your-responsibilities"
              variant="h3"
            >
              4. Your Responsibilities
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              4.1 Your Accounts. Except to the extent caused by our breach of this Agreement, (a) you are responsible for all activities that occur under your account, regardless of whether the activities are authorized by you or undertaken by you, your employees or a third party (including your contractors, agents, or End Users), and (b) we and our affiliates are not responsible for unauthorized access to your account.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              4.2 Your Content. You will ensure that Your Content and your and End Users’ use of Your Content or the OnData Services will not violate any applicable laws. You are solely responsible for the development, operation, maintenance, and use of Your Content.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              4.3 Your Security and Backup. You are responsible for properly configuring and using the OnData Services and otherwise taking appropriate action to secure, protect and backup your accounts and Your Content in a manner that will provide appropriate security and protection, which might include use of encryption to protect Your Content from unauthorized access and routinely archiving Your Content.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              4.4 Log-In Credentials and Account Keys. OnData log-in credentials and private keys generated by the OnData Services are for your internal use only and you will not sell, transfer, or sublicense them to any other entity or person, except that you may disclose your private key to your agents and subcontractors performing work on your behalf.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              4.5 End Users. You will be deemed to have taken any action that you permit, assist, or facilitate any person or entity to take related to this Agreement, Your Content or use of the OnData Services. You are responsible for End Users’ use of Your Content and the OnData Services. You will ensure that all End Users comply with your obligations under this Agreement and that the terms of your agreement with each End User are consistent with this Agreement. If you become aware of any violation of your obligations under this Agreement caused by an End User, you will immediately suspend access to Your Content and the OnData Services by such End User.
            </Typography>

            <Typography
              color="textPrimary"
              id="fees-and-payment"
              variant="h3"
            >
              5. Fees and Payment
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              5.1 Service Fees. We calculate and bill subscription fees and charges annually. We may bill you more frequently for fees accrued if we suspect that your account is fraudulent or at risk of non-payment. You will pay us the applicable fees and charges for use of the OnData Services using one of the payment methods we support. All amounts payable by you under this Agreement will be paid to us without setoff or counterclaim, and without any deduction or withholding. We may increase or add new fees and charges for any existing Services you are using by giving you at least 30-day prior notice. We may elect to charge you interest at the rate of 1.5% per month (or the highest rate permitted by law, if less) on all late payments.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              5.2 Taxes. Each party will be responsible, as required under applicable law, for identifying and paying all taxes and other governmental fees and charges (and any penalties, interest, and other additions thereto) that are imposed on that party upon or with respect to the transactions and payments under this Agreement. All fees payable by you are exclusive of taxes, except where applicable law requires otherwise. We may charge and you will pay applicable taxes that we are legally obligated or authorized to collect from you. You will provide such information to us as reasonably required to determine whether we are obligated to collect taxes from you. We will not collect, and you will not pay, any tax for which you furnish us a properly completed exemption certificate or a direct payment permit certificate for which we may claim an available exemption from such tax. All payments made by you to us under this Agreement will be made free and clear of any deduction or withholding, as may be required by law. If any such deduction or withholding is required on any payment, you will pay such additional amounts as are necessary so that the net amount received by us is equal to the amount then due and payable under this Agreement. We will provide you with such tax forms as are reasonably requested to reduce or eliminate the amount of any withholding or deduction for taxes in respect of payments made under this Agreement.
            </Typography>

            <Typography
              color="textPrimary"
              id="temporary-suspension"
              variant="h3"
            >
              6. Temporary Suspension
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              6.1 Generally. We may suspend your or any End User’s right to access or use any portion or all of the OnData Services immediately upon notice to you if we determine:
            </Typography>

            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul className="unstyled">
                <li>
                  (a) your or an End User’s use of the OnData Services (i) poses a security risk to the OnData Services or any third party, (ii) could adversely impact our systems, the OnData Services, or the systems or Content of any other OnData customer, (iii) could subject us, our affiliates, or any third party to liability, or (iv) could be fraudulent;
                </li>
                <li>
                  (b) you are, or any End User is, in breach of this Agreement;
                </li>
                <li>
                  (c) you are in breach of your payment obligations under Section 5; or
                </li>
                <li>
                  (d) you have ceased to operate in the ordinary course, made an assignment for the benefit of creditors or similar disposition of your assets, or become the subject of any bankruptcy, reorganization, liquidation, dissolution, or similar proceeding.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              6.2 Effect of Suspension. If we suspend your right to access or use any portion or all of the OnData Services:
            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul className="unstyled">
                <li>
                  (a) you remain responsible for all fees and charges you incur during the period of suspension; and
                </li>

                <li>
                  (b) you will not be entitled to any service credits under the Service Level Agreements for any period of suspension.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              id="agreement-term-and-termination"
              variant="h3"
            >
              7. Agreement Term and Termination
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              7.1 Term. The term of this Agreement will commence on the Effective Date and will remain in effect until terminated under this Section 7. Any notice of termination of this Agreement by either party to the other must include a Termination Date that complies with the notice periods in Section 7.2.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              7.2 Termination.
            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul className="unstyled">
                <li>
                  (a) Termination for Convenience. You may terminate this Agreement for any reason by providing us notice and closing your account for all OnData Services for which we provide an account closing mechanism. We may terminate this Agreement for any reason by providing you at least 30-day advance notice.
                </li>

                <li>
                  (b) Termination for Cause.
                  <ul className="unstyled">
                    <li>
                      (i) By Either Party. Either party may terminate this Agreement for cause if the other party is in material breach of this Agreement and the material breach remains uncured for a period of 30 days from receipt of notice by the other party. No later than the Termination Date, you will close your account.
                    </li>
                    <li>
                      (ii) By Us. We may also terminate this Agreement immediately upon notice to you either for cause if we have the right to suspend under Section 6 or to comply with the law or requests of governmental entities.
                    </li>
                  </ul>

                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              7.3 Effect of Termination.
            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul className="unstyled">
                <li>
                  (a) Generally. Upon the Termination Date:
                  <ul className="unstyled">
                    <li>
                      (i) except as provided in Section 7.3(b), all your rights under this Agreement immediately terminate;
                    </li>
                    <li>
                      (ii) you remain responsible for all fees and charges you have incurred through the Termination Date and are responsible for any fees and charges you incur during the post-termination period described in Section 7.3(b);
                    </li>
                    <li>
                      (iii) you will immediately return or, if instructed by us, destroy all OnData Content in your possession; and
                    </li>
                    <li>
                      (iv) Sections 4.1, 5, 7.3, 8 (except the license granted to you in Section 8.3), 9, 10, 11, and 13 will continue to apply in accordance with their terms.
                    </li>
                  </ul>
                </li>

                <li>
                  (b) Post-Termination. Unless we terminate your use of the OnData Services pursuant to Section 7.2(b), during the 30 days following the Termination Date:
                  <ul className="unstyled">
                    <li>
                      (i) we will not take action to remove from the OnData systems any of Your Content as a result of the termination; and
                    </li>

                    <li>
                      (ii) we will allow you to retrieve Your Content from the OnData Services only if you have paid all amounts due under this Agreement.
                    </li>
                  </ul>
                </li>
              </ul>

              For any use of the OnData Services after the Termination Date, the terms of this Agreement will apply and you will pay the applicable fees at the rates under Section 5.
            </Typography>

            <Typography
              color="textPrimary"
              id="proprietary-rights"
              variant="h3"
            >
              8. Proprietary Rights
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              8.1 Your Content. Except as provided in this Section 8, we obtain no rights under this Agreement from you (or your licensors) to Your Content. You consent to our use of Your Content to provide the OnData Services to you and any End Users.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              8.2 Adequate Rights. You represent and warrant to us that: (a) you or your licensors own all right, title, and interest in and to Your Content and Suggestions; and (b) you have all rights in Your Content and Suggestions necessary to grant the rights contemplated by this Agreement.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              8.3 OnData License. We or our licensors own all right, title, and interest in and to the OnData Services, and all related technology and intellectual property rights. Subject to the terms of this Agreement, we grant you a limited, revocable, non-exclusive, non-sublicensable, non-transferrable license to do the following: (a) access and use the OnData Services solely in accordance with this Agreement; and (b) copy and use the OnData Content solely in connection with your permitted use of the OnData Services. Except as provided in this Section 8.3, you obtain no rights under this Agreement from us or our affiliates to the OnData Services, including any related intellectual property rights. Some OnData Content and Third-Party Content may be provided to you under a separate license, such as the Apache License, Version 2.0, or other open-source license. In the event of a conflict between this Agreement and any separate license, the separate license will prevail with respect to the OnData Content or Third-Party Content that is the subject of such separate license.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              8.4 License Restrictions. Neither you nor any End User will use the OnData Services in any manner or for any purpose other than as expressly permitted by this Agreement. Neither you nor any End User will, or will attempt to (a) modify, distribute, alter, tamper with, repair, or otherwise create derivative works of any Content included in the OnData Services, (b) reverse engineer, disassemble, or decompile the OnData Content or apply any other process or procedure to derive the source code of any software included in the OnData Services, (c) access or use the OnData Services in a way intended to avoid incurring fees or exceeding usage limits or quotas, or (d) resell or sublicense the OnData Service. You will not misrepresent or embellish the relationship between us and you (including by expressing or implying that we support, sponsor, endorse, or contribute to you or your business endeavors). You will not imply any relationship or affiliation between us and you except as expressly permitted by this Agreement.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              8.5 Suggestions. If you provide any Suggestions to us, we will be entitled to use the Suggestions without restriction. You hereby irrevocably assign to us all right, title, and interest in and to the Suggestions and agree to provide us any assistance we require to document, perfect, and maintain our rights in the Suggestions.
            </Typography>

            <Typography
              color="textPrimary"
              id="indemnification"
              variant="h3"
            >
              9. Indemnification
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              9.1 General. You will defend, indemnify, and hold harmless us, our affiliates and licensors, and each of their respective employees, officers, directors, and representatives from and against any Losses arising out of or relating to any third-party claim concerning: (a) your or any End Users’ use of the OnData Services (including any activities under your OnData account and use by your employees and personnel); (b) breach of this Agreement or violation of applicable law by you, End Users or Your Content; or (c) a dispute between you and any End User. You will reimburse us for reasonable attorneys’ fees, as well as our employees’ and contractors’ time and materials spent responding to any third-party subpoena or other compulsory legal order or process associated with third party claims described in (a) through (c) above at our then-current hourly rates.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              9.2 Intellectual Property.
            </Typography>
            <Typography
              color="textPrimary"
              component="span"
              variant="body1"
            >
              <ul className="unstyled">
                <li>
                  (a) Subject to the limitations in this Section 9, OnData will defend you and your employees, officers, and directors against any third-party claim alleging that the OnData Services infringe or misappropriate that third party’s intellectual property rights and will pay the amount of any adverse final judgment or settlement.
                </li>
                <li>
                  (b) Subject to the limitations in this Section 9, you will defend OnData, its affiliates, and their respective employees, officers, and directors against any third-party claim alleging that any of Your Content infringes or misappropriates that third party’s intellectual property rights and will pay the amount of any adverse final judgment or settlement.
                </li>
                <li>
                  (c) Neither party will have obligations or liability under this Section 9.2 arising from infringement by combinations of the OnData Services or Your Content, as applicable, with any other product, service, software, data, content, or method. In addition, OnData will have no obligations or liability arising from your or any End User’s use of the Services after OnData has notified you to discontinue such use. The remedies provided in this Section 9.2 are the sole and exclusive remedies for any third-party claims of infringement or misappropriation of intellectual property rights by the Services or by Your Content.
                </li>
                <li>
                  (d) For any claim covered by Section 9.2(a), OnData will, at its election, either: (i) procure the rights to use that portion of the Services alleged to be infringing; (ii) replace the alleged infringing portion of the Services with a non-infringing alternative; (iii) modify the alleged infringing portion of the Services to make it non-infringing; or (iv) terminate the allegedly infringing portion of the Services or this Agreement.
                </li>
              </ul>
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              9.3 Process. The obligations under this Section 9 will apply only if the party seeking defense or indemnity: (a) gives the other party prompt written notice of the claim; (b) permits the other party to control the defense and settlement of the claim; and (c) reasonably cooperates with the other party (at the other party’s expense) in the defense and settlement of the claim. In no event will a party agree to any settlement of any claim that involves any commitment, other than the payment of money, without the written consent of the other party.
            </Typography>

            <Typography
              color="textPrimary"
              id="disclaimers"
              variant="h3"
            >
              10. Disclaimers
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              THE ONDATA SERVICES ARE PROVIDED “AS IS.” EXCEPT TO THE EXTENT PROHIBITED BY LAW, OR TO THE EXTENT ANY STATUTORY RIGHTS APPLY THAT CANNOT BE EXCLUDED, LIMITED OR WAIVED, WE AND OUR AFFILIATES (A) MAKE NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE REGARDING THE ONDATA SERVICES, AND (B) DISCLAIM ALL WARRANTIES, INCLUDING ANY IMPLIED OR EXPRESS WARRANTIES (I) OF MERCHANTABILITY, SATISFACTORY QUALITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR QUIET ENJOYMENT, (II) ARISING OUT OF ANY COURSE OF DEALING OR USAGE OF TRADE, (III) THAT THE ONDATA SERVICES WILL BE UNINTERRUPTED, ERROR FREE OR FREE OF HARMFUL COMPONENTS, AND (IV) THAT ANY CONTENT WILL BE SECURE OR NOT OTHERWISE LOST OR ALTERED.
            </Typography>

            <Typography
              color="textPrimary"
              id="limitations-of-liability"
              variant="h3"
            >
              11. Limitations of Liability
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              WE AND OUR AFFILIATES WILL NOT BE LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES (INCLUDING DAMAGES FOR LOSS OF PROFITS, REVENUES, CUSTOMERS, OPPORTUNITIES, GOODWILL, USE, OR DATA), EVEN IF A PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. FURTHER, NEITHER WE NOR ANY OF OUR AFFILIATES WILL BE RESPONSIBLE FOR ANY COMPENSATION, REIMBURSEMENT, OR DAMAGES ARISING IN CONNECTION WITH: (A) YOUR INABILITY TO USE THE ONDATA SERVICES, INCLUDING AS A RESULT OF ANY (I) TERMINATION OR SUSPENSION OF THIS AGREEMENT OR YOUR USE OF OR ACCESS TO THE ONDATA SERVICES, (II) OUR DISCONTINUATION OF ANY OR ALL OF THE ONDATA SERVICES, OR, (III) WITHOUT LIMITING ANY OBLIGATIONS UNDER THE SERVICE LEVEL AGREEMENTS, ANY UNANTICIPATED OR UNSCHEDULED DOWNTIME OF ALL OR A PORTION OF THE SERVICES FOR ANY REASON; (B) THE COST OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; (C) ANY INVESTMENTS, EXPENDITURES, OR COMMITMENTS BY YOU IN CONNECTION WITH THIS AGREEMENT OR YOUR USE OF OR ACCESS TO THE ONDATA SERVICES; OR (D) ANY UNAUTHORIZED ACCESS TO, ALTERATION OF, OR THE DELETION, DESTRUCTION, DAMAGE, LOSS OR FAILURE TO STORE ANY OF YOUR CONTENT OR OTHER DATA. IN ANY CASE, EXCEPT FOR PAYMENT OBLIGATIONS UNDER SECTION 9.2, OUR AND OUR AFFILIATES’ AGGREGATE LIABILITY UNDER THIS AGREEMENT WILL NOT EXCEED THE AMOUNT YOU ACTUALLY PAY US UNDER THIS AGREEMENT FOR THE SERVICE THAT GAVE RISE TO THE CLAIM DURING THE 12 MONTHS BEFORE THE LIABILITY AROSE. THE LIMITATIONS IN THIS SECTION 11 APPLY ONLY TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
            </Typography>

            <Typography
              color="textPrimary"
              id="modifications-and-updates"
              variant="h3"
            >
              12. Modifications and Updates
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              We may modify this Agreement at any time by posting a revised version on the OnData website or by otherwise notifying you in accordance with Section 13.10; provided, however, that we will provide at least 90-day advance notice in accordance with Section 13.10 for adverse changes to any Service Level Agreement. Subject to the 90-day advance notice requirement with respect to adverse changes to Service Level Agreements, the modified terms will become effective upon posting or, if we notify you by email, as stated in the email message. By continuing to use the OnData Services after the effective date of any modifications to this Agreement, you agree to be bound by the modified terms. It is your responsibility to check the OnData website regularly for modifications to this Agreement. We last modified this Agreement on the date listed at the beginning of this Agreement.
            </Typography>

            <Typography
              color="textPrimary"
              id="miscellaneous"
              variant="h3"
            >
              13. Miscellaneous
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.1 Assignment. You will not assign or otherwise transfer this Agreement or any of your rights and obligations under this Agreement, without our prior written consent from OnData. Any assignment or transfer in violation of this Section 13.1 will be void. We may assign this Agreement without your consent (a) in connection with a merger, acquisition, or sale of all or substantially all of our assets, or (b) to any affiliate or as part of a corporate reorganization; and effective upon such assignment, the assignee is deemed substituted for OnData as a party to this Agreement and OnData is fully released from all of its obligations and duties to perform under this Agreement. Subject to the foregoing, this Agreement will be binding upon, and inure to the benefit of the parties and their respective permitted successors and assigns.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.2 Entire Agreement. This Agreement is the entire agreement between you and us regarding the subject matter of this Agreement. This Agreement supersedes all prior or contemporaneous representations, understandings, agreements, or communications between you and us, whether written or verbal, regarding the subject matter of this Agreement.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.3 Force Majeure. We and our affiliates will not be liable for any delay or failure to perform any obligation under this Agreement where the delay or failure results from any cause beyond our reasonable control, including acts of God, labor disputes or other industrial disturbances, electrical or power outages, utilities or other telecommunications failures, earthquake, storms or other elements of nature, blockages, embargoes, riots, acts or orders of government, acts of terrorism, or war.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.4 Governing Law for Disputes. The laws of the State of Texas, without reference to conflict of law rules, govern this Agreement and any dispute of any sort that might arise between you and us. Any dispute or claim relating in any way to your use of the OnData Services will be adjudicated in the court at Travis County, Texas.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.5 Independent Contractors. We and you are independent contractors, and this Agreement will not be construed to create a partnership, joint venture, agency, or employment relationship. Neither party, nor any of their respective affiliates, is an agent of the other for any purpose or has the authority to bind the other.
            </Typography>

            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.6 No Waivers. The failure by us to enforce any provision of this Agreement will not constitute a present or future waiver of such provision nor limit our right to enforce such provision at a later time. All waivers by us must be in writing to be effective.
            </Typography>
            <Typography
              color="textPrimary"
              variant="body1"
            >
              13.7 Severability. If any portion of this Agreement is held to be invalid or unenforceable, the remaining portions of this Agreement will remain in full force and effect. Any invalid or unenforceable portions will be interpreted to effect and intent of the original portion. If such construction is not possible, the invalid or unenforceable portion will be severed from this Agreement, but the rest of the Agreement will remain in full force and effect.
            </Typography>

          </LegalTextContent>
        </Container>
      </Box>
    </LayoutPlain>
  )
}

export default CustomerAgreement;
